import React, { useState } from 'react';
import { Shield, Zap, Brain, Code } from 'lucide-react';
import { ScrollReveal, StaggerContainer, StaggerItem } from '@/components/ui/ScrollReveal';
import { Container, Section } from '@/components/layout/Container';
import { StatCard } from '@/components/ui/Card';

const expertiseAreas = [
  {
    icon: <Shield className="w-5 h-5" />,
    title: 'Clinical Authority',
    description: '10+ years independent clinical responsibility with extensive emergency medicine exposure.',
  },
  {
    icon: <Zap className="w-5 h-5" />,
    title: 'Emergency Medicine',
    description: 'Thousands of hours in high-pressure medical decision environments.',
  },
  {
    icon: <Brain className="w-5 h-5" />,
    title: 'Psychiatric Care',
    description: 'Outpatient treatment experience with complex multimorbidity management.',
  },
  {
    icon: <Code className="w-5 h-5" />,
    title: 'Health-Tech',
    description: 'Hands-on development of AI-assisted healthcare tools and infrastructure.',
  },
];

const credentials = [
  { value: '10+', label: 'Years Clinical Practice', suffix: '' },
  { value: 'MD', label: 'Specialist Certification', suffix: '' },
  { value: '1000s', label: 'Emergency Hours', suffix: '+' },
  { value: '4', label: 'Languages', suffix: '' },
];

export const ExecutiveProfile: React.FC = () => {
  const [imageError, setImageError] = useState(false);

  return (
    <Section id="profile" dark>
      <Container>
        {/* Section Header */}
        <ScrollReveal className="text-center mb-16">
          <span className="text-caption text-apple-blue uppercase tracking-wider font-medium">
            Executive Profile
          </span>
          <h2 className="text-h1 md:text-display-m font-bold mt-4 mb-6">
            Operating at the <span className="gradient-text">Intersection</span>
          </h2>
          <p className="text-body-l text-text-secondary max-w-2xl mx-auto">
            Specialist in Family Medicine. Extensive emergency exposure. Someone who builds the software himself.
          </p>
        </ScrollReveal>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Image & Stats */}
          <ScrollReveal direction="left">
            <div className="relative min-w-0">
              {/* Profile image container */}
              <div className="relative aspect-[4/5] max-w-md mx-auto lg:mx-0 rounded-3xl overflow-hidden">
                {/* Background glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-apple-blue/20 to-transparent" />
                
                {!imageError ? (
                  <img
                    src="/assets/christer.png"
                    alt="Christer Torgilstveit Larsen, MD"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-bg-tertiary">
                    <div className="text-center">
                      <div className="w-28 h-28 mx-auto mb-3 rounded-full bg-bg-secondary border-2 border-apple-blue/30 flex items-center justify-center">
                        <span className="text-3xl font-bold text-apple-blue">CTL</span>
                      </div>
                      <p className="text-body-s text-text-tertiary">Christer T. Larsen, MD</p>
                    </div>
                  </div>
                )}

                {/* Border frame */}
                <div className="absolute inset-0 border border-border-subtle rounded-3xl" />
                
                {/* Corner accents */}
                <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-apple-blue/50 rounded-tl-lg" />
                <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-apple-blue/50 rounded-br-lg" />
              </div>

              {/* Stats floating card */}
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[min(92%,22rem)] sm:w-auto sm:left-auto sm:translate-x-0 sm:-right-4 lg:right-8 bg-bg-tertiary/95 backdrop-blur-xl rounded-2xl border border-border-subtle p-5 sm:p-6 shadow-2xl">
                <div className="grid grid-cols-2 gap-6">
                  {credentials.slice(0, 2).map((stat) => (
                    <StatCard key={stat.label} {...stat} />
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Right - Content */}
          <div>
            <ScrollReveal>
              <h3 className="text-h2 font-semibold mb-6">
                Board-Level Medical Advisory
              </h3>
              <p className="text-body-l text-text-secondary mb-6">
                Norwegian licensed physician, Specialist in Family Medicine, with over a decade
                of clinical practice spanning emergency medicine, psychiatric outpatient care,
                and military leadership training. Also, unusually: I write the code myself.
              </p>
              <p className="text-body text-text-tertiary mb-8">
                Most medical advisors hand off to the engineering team and hope for the best.
                I've built AI-assisted healthcare tools independently — which means I can sit
                in a technical architecture review and a clinical governance meeting on the
                same day and speak the same language in both.
              </p>
            </ScrollReveal>

            {/* Expertise Grid */}
            <StaggerContainer className="grid sm:grid-cols-2 gap-4">
              {expertiseAreas.map((area) => (
                <StaggerItem key={area.title}>
                  <div className="flex gap-4 p-4 rounded-xl bg-bg-primary/50 border border-border-subtle hover:border-apple-blue/30 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-apple-blue/10 flex items-center justify-center flex-shrink-0">
                      <div className="text-apple-blue">{area.icon}</div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-text-primary mb-1">{area.title}</h4>
                      <p className="text-body-s text-text-tertiary">{area.description}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>

            {/* Additional stats row */}
            <ScrollReveal delay={0.4}>
              <div className="flex flex-wrap gap-6 sm:gap-12 mt-8 pt-8 border-t border-border-subtle">
                {credentials.slice(2).map((stat) => (
                  <StatCard key={stat.label} {...stat} />
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </Container>
    </Section>
  );
};

export default ExecutiveProfile;
